/* eslint-disable max-statements */

import { UseFormReturnType } from '@mantine/form';

export const TYPE_OPTIONS = [
  { value: 'an', label: 'Alphanumeric' },
  { value: 'a', label: 'Alpha' },
  { value: 'n', label: 'Numeric' },
];

export const MODE_OPTIONS = [
  { value: 'mapping', label: 'Mapping Field', fieldLabel: 'File Column Name' },
  { value: 'variable', label: 'Variable', fieldLabel: 'Field Column Name / Variable Name' },
  { value: 'itemVariable', label: 'Item Variable', fieldLabel: 'Variable Name' },
];

export const constants = {
  DEFAULT_MAX_ROWS: 9999,
};

export interface TemplateFieldState {
  mode?: 'mapping' | 'variable' | 'itemVariable';
  ignore: boolean;
  fileColumnName: string;
  property: string;
  type: string;
  min?: number;
  max?: number;
  trim: boolean;
  wcoFormat?: boolean;
  hasDecimal: boolean;
  decimal?: number;
  autoRound: boolean;
  optional: boolean;
  hasDefaultValue: boolean;
  defaultValue?: string;
}

export interface TemplateFieldProps {
  form: UseFormReturnType<any>;
  arrayPath: string;
  disabled?: boolean;
  allowManualOrdering?: boolean;
  maxRows?: number;
  onChange?: (values: string[], arrayPath: string) => void;
  propertyOptions?: string[];
  // propertyOptions?: { value: string; label: string }[];
}

export const createInitialState = (): TemplateFieldState => ({
  mode: 'mapping',
  ignore: false,
  fileColumnName: '',
  property: '',
  type: 'an',
  min: 0,
  max: 1,
  trim: false,
  wcoFormat: false,
  hasDecimal: false,
  decimal: undefined,
  autoRound: false,
  optional: false,
  hasDefaultValue: false,
  defaultValue: '',
});

export const deconstructString = (rawFieldText: string): TemplateFieldState => {
  const [fileColumnName, mappingValues] = rawFieldText.split(':');

  if (mappingValues !== '*ignore*') {
    const [property, ...otherProperties] = mappingValues.split('#');

    const [variable, propertiesAndDefaultValues] = otherProperties.reduce(
      (acc, value) => {
        if (value === 'variable' || value === 'itemVariable') {
          acc[0] = value;
        } else {
          acc[1] = value;
        }

        return acc;
      },
      ['', ''],
    );

    if (!propertiesAndDefaultValues) {
      return Object.assign(createInitialState(), {
        fileColumnName,
        mode: (variable || 'mapping') as any,
        property,
      });
    }

    const [properties, defaultValue] = propertiesAndDefaultValues.split('&dv=');
    const templateDefaultValue = typeof defaultValue !== 'undefined' ? defaultValue : '';

    const [mappingType, initialLimits] = properties.split('-');
    let limits = initialLimits;

    const optional = limits.includes('o');
    limits = limits.replace('o', '');
    const autoRound = limits.includes('r');
    limits = limits.replace('r', '');

    const [min, initialMax] = limits.split('..');
    let max = initialMax;

    let trim = false;
    let wcoFormat = false;
    if (max.includes('t')) {
      max = max.replace('t', '');
      trim = true;
    }
    if (max.includes('w')) {
      max = max.replace('w', '');
      wcoFormat = true;
    }

    const [maxValue, decimal] = max.split(',');

    return {
      fileColumnName,
      property,
      type: mappingType,
      mode: (variable || 'mapping') as any,
      optional,
      min: !Number.isNaN(Number(min)) ? Number(min) : 0,
      max: !Number.isNaN(Number(maxValue)) ? Number(maxValue) : 1,
      trim,
      wcoFormat,
      autoRound,
      decimal: decimal && !Number.isNaN(Number(decimal)) ? Number(decimal) : null,
      hasDecimal: decimal ? true : false,
      ignore: false,
      defaultValue: templateDefaultValue?.toString() || '',
      hasDefaultValue: propertiesAndDefaultValues.includes('&dv='),
    };
  }

  return Object.assign(createInitialState(), {
    fileColumnName,
    ignore: true,
  });
};

export const buildString = (field: TemplateFieldState): string => {
  if (field.mode === 'variable') {
    return `${field.fileColumnName}:${field.property}#variable`;
  }
  if (field.mode === 'itemVariable') {
    return `${field.fileColumnName}:${field.property}#itemVariable`;
  }
  if (field.ignore) {
    return `${field.fileColumnName}:*ignore*`;
  }
  let str = `${field.fileColumnName}:${field.property}#${field.type}-${field.min ?? 0}..${field.max ?? 1}`;

  if (field.trim) str += 't';
  if (field.wcoFormat) str += 'w';

  if (field.hasDecimal) {
    str += `,${field.decimal}`;
    if (field.autoRound) str += 'r';
  }
  if (field.optional) str += 'o';

  if (field.hasDefaultValue) {
    str += `&dv=${field.defaultValue}`;
  }
  return str;
};
